import { useEffect, useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useUser } from '../hooks/useUser';
import { selectCurrentUser } from '../../../app/store/userSelectors';
import { useNavigate, useLocation } from 'react-router-dom';
import { rememberOpenAuthModal } from '../../../app/components/auth/authModalStorage';
import { isTelegramWebApp } from '../../../app/utils/telegramUtils';
import { selectAuthStatus } from '../../../app/store/authSelectors';
import { getRefreshToken, getAccessToken, setAccessToken, setRefreshToken } from '../../../app/utils/authStorage';
import { getTokenAuthType } from '../../../app/utils/authToken';
import { useAuth } from '../../auth/hooks/useAuth';

const UserGuard = ({ children }) => {
    const authStatus = useSelector(selectAuthStatus);
    const userRedux = useSelector(selectCurrentUser);
    const { error, refetchUser } = useUser();
    const navigate = useNavigate();
    const location = useLocation();
    const [hasTriedRefresh, setHasTriedRefresh] = useState(false);
    const [hasTriedMiniAppAuth, setHasTriedMiniAppAuth] = useState(false);
    const { loginWithTelegram } = useAuth();
    const inMiniApp = isTelegramWebApp();

    const redirectToAuth = useCallback(() => {
        if (!inMiniApp) {
            rememberOpenAuthModal('login');
            navigate('/?auth=login', { state: { from: location, openAuth: 'login' }, replace: true });
        }
    }, [inMiniApp, navigate, location]);

    const redirectToLinkTelegram = useCallback(() => {
        if (!inMiniApp && location.pathname !== '/link-telegram') {
            navigate('/link-telegram', { state: { from: location }, replace: true });
        }
    }, [inMiniApp, location, navigate]);

    const handleTokenRefresh = useCallback(async () => {
        if (hasTriedRefresh) return false;
        
        setHasTriedRefresh(true);
        const refreshToken = getRefreshToken();
        
        if (!refreshToken) {
            if (inMiniApp && !hasTriedMiniAppAuth) {
                setHasTriedMiniAppAuth(true);
                try {
                    await loginWithTelegram();
                    await refetchUser();
                    return true;
                } catch (err) {
                    console.error('MiniApp auth failed in UserGuard:', err);
                }
            }
            redirectToAuth();
            return false;
        }

        try {
            const response = await fetch(`${process.env.REACT_APP_AUTH_SERVICE_URL}/v1/refresh`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ refresh_token: refreshToken }),
            });

            if (response.ok) {
                const data = await response.json();
                setAccessToken(data.access_token);
                setRefreshToken(data.refresh_token);
                
                await refetchUser();
                return true;
            }
        } catch (err) {
            console.error('Token refresh failed:', err);
        }

        if (inMiniApp && !hasTriedMiniAppAuth) {
            setHasTriedMiniAppAuth(true);
            try {
                await loginWithTelegram();
                await refetchUser();
                return true;
            } catch (err) {
                console.error('MiniApp auth failed in UserGuard:', err);
            }
        }
        redirectToAuth();
        return false;
    }, [hasTriedRefresh, hasTriedMiniAppAuth, redirectToAuth, refetchUser, inMiniApp, loginWithTelegram]);

    useEffect(() => {
        if (inMiniApp) return;

        const token = getAccessToken();
        if (!token) {
            redirectToAuth();
            return;
        }

        if (getTokenAuthType(token) === 'credential') {
            redirectToLinkTelegram();
        }
    }, [inMiniApp, redirectToAuth, redirectToLinkTelegram]);

    useEffect(() => {
        if (error?.status === 401 && !hasTriedRefresh) {
            handleTokenRefresh();
        } else if (!inMiniApp && error?.status === 403 && getTokenAuthType(getAccessToken()) === 'credential') {
            redirectToLinkTelegram();
        } else if (!inMiniApp && error?.status && error.status !== 401 && authStatus !== 'succeeded') {
            redirectToAuth();
        }
    }, [error, authStatus, hasTriedRefresh, handleTokenRefresh, inMiniApp, redirectToAuth, redirectToLinkTelegram]);

    if (error && error.status !== 401 && error.status !== 403) {
        return null;
    }

    if (!inMiniApp && getTokenAuthType(getAccessToken()) === 'credential') {
        return null;
    }

    if (!userRedux) {
        return null;
    }

    return children;
};

export default UserGuard;
